import { Bandeau, Bouton, CarteTerritoire, Champ, Selection, type Territoire } from '@cc/ui';
import type { FeatureCollection, LineString, Point } from 'geojson';
import { useMemo, useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { INVALIDER_MARCHES } from './api';
import { EditeurRangee } from './EditeurRangee';
import { ImportAllees } from './ImportAllees';
import { creerAllee, deplacerPlace, useAllees, usePlaces } from './geographie-api';
import type { Emplacement, MarcheDetail } from './types';
import styles from './marches.module.css';

type Bbox = [number, number, number, number];
type Position = [number, number];

function empriseInitiale(m: MarcheDetail): Bbox | null {
  const points = m.emplacements.flatMap((e) =>
    e.position ? [[e.position.longitude, e.position.latitude] as Position] : [],
  );
  if (m.position) points.push([m.position.longitude, m.position.latitude]);
  if (!points.length) return null;
  return [
    Math.min(...points.map((p) => p[0])) - 0.001,
    Math.min(...points.map((p) => p[1])) - 0.001,
    Math.max(...points.map((p) => p[0])) + 0.001,
    Math.max(...points.map((p) => p[1])) + 0.001,
  ];
}

export function PlanGeographique({
  marche,
  edition,
  selection,
  emplacements,
  onSelection,
}: {
  marche: MarcheDetail;
  edition: boolean;
  selection: Emplacement | null;
  emplacements: Emplacement[];
  onSelection: (id: string) => void;
}) {
  const initiale = useMemo(() => empriseInitiale(marche), [marche]);
  const [bbox, setBbox] = useState<Bbox | null>(initiale);
  const [precision, setPrecision] = useState('toutes');
  const [tracer, setTracer] = useState(false);
  const [ligne, setLigne] = useState<Position[]>([]);
  const [nom, setNom] = useState('');
  const [position, setPosition] = useState<Position | null>(null);
  const allees = useAllees(marche.id);
  const places = usePlaces(marche.id, bbox);
  const creer = useAction({
    executer: () =>
      creerAllee(marche.id, { nom, ligne: { type: 'LineString', coordinates: ligne } }),
    succes: 'Allée enregistrée.',
    invalider: INVALIDER_MARCHES,
    apres: () => {
      setLigne([]);
      setTracer(false);
      setNom('');
    },
  });
  const deplacer = useAction({
    executer: () =>
      deplacerPlace(selection!.id, {
        position: { longitude: position![0], latitude: position![1] },
        version_attendue: selection!.position_version ?? 0,
      }),
    succes: 'Position schématique enregistrée.',
    invalider: INVALIDER_MARCHES,
    apres: () => setPosition(null),
  });
  const territoire = useMemo<Territoire | null>(
    () =>
      initiale
        ? {
            id: marche.id,
            niveau: 'commune',
            libelle: marche.nom,
            geometrie: {
              type: 'Polygon',
              coordinates: [
                [
                  [initiale[0], initiale[1]],
                  [initiale[2], initiale[1]],
                  [initiale[2], initiale[3]],
                  [initiale[0], initiale[3]],
                  [initiale[0], initiale[1]],
                ],
              ],
            },
            indicatif: true,
            source: 'Emprise indicative des positions enregistrées',
            voisins: [],
          }
        : null,
    [initiale, marche.id, marche.nom],
  );
  const points = useMemo<FeatureCollection<Point>>(() => {
    const visibles = new Set(emplacements.map((e) => e.id));
    return {
      type: 'FeatureCollection',
      features: (places.data?.features ?? []).filter(
        (f) =>
          visibles.has(String(f.properties?.id)) &&
          (precision === 'toutes' || f.properties?.precision === precision),
      ),
    };
  }, [places.data, emplacements, precision]);
  const lignes = useMemo<FeatureCollection<LineString>>(
    () => ({
      type: 'FeatureCollection',
      features: [
        ...(allees.data ?? []).map((a) => ({
          type: 'Feature' as const,
          id: a.id,
          geometry: a.ligne,
          properties: { nom: a.nom },
        })),
        ...(ligne.length > 1
          ? [
              {
                type: 'Feature' as const,
                id: 'trace-en-cours',
                geometry: { type: 'LineString' as const, coordinates: ligne },
                properties: { nom: 'Tracé en cours' },
              },
            ]
          : []),
      ],
    }),
    [allees.data, ligne],
  );

  return (
    <div className={styles.pile}>
      <Selection
        libelle="Précision des positions"
        value={precision}
        onChange={(e) => setPrecision(e.target.value)}
        options={[
          { valeur: 'toutes', libelle: 'Toutes les positions' },
          { valeur: 'schematique', libelle: 'Schématiques' },
          { valeur: 'terrain', libelle: 'Relevées sur le terrain' },
        ]}
      />
      {places.error || allees.error ? (
        <Bandeau type="erreur">{messageErreur(places.error ?? allees.error)}</Bandeau>
      ) : null}
      {territoire ? (
        <CarteTerritoire
          titre="Places du marché"
          echelles={[territoire]}
          echelle={territoire.id}
          onEchelle={() => undefined}
          compact
          couchePoints={{
            points,
            allees: lignes,
            onPoint: (id) => {
              setPosition(null);
              onSelection(id);
            },
            onEmprise: setBbox,
          }}
          onSaisie={
            edition
              ? (p) => {
                  if (tracer) setLigne((l) => [...l, p]);
                  else if (selection) setPosition(p);
                }
              : undefined
          }
        />
      ) : (
        <Bandeau type="info">
          Renseignez la position du marché pour ouvrir son plan géographique.
        </Bandeau>
      )}
      <p className={styles.aide}>
        Les places schématiques sont à confirmer sur le terrain. La liste des emplacements reste
        disponible sans carte.
      </p>
      {edition ? (
        <>
          <div className={styles.barreFiltres}>
            <Bouton
              variante="secondaire"
              aria-pressed={tracer}
              onClick={() => {
                setTracer(!tracer);
                setPosition(null);
              }}
            >
              Tracer une allée
            </Bouton>
            {tracer ? (
              <Bouton
                variante="discret"
                disabled={!ligne.length}
                onClick={() => setLigne((l) => l.slice(0, -1))}
              >
                Retirer le dernier point
              </Bouton>
            ) : null}
          </div>
          {tracer ? (
            <form
              className={styles.pile}
              onSubmit={(e) => {
                e.preventDefault();
                creer.lancer(undefined);
              }}
            >
              <p>Cliquez sur la carte pour dessiner l’allée ({ligne.length} points).</p>
              <Champ
                libelle="Nom de l’allée"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                required
                maxLength={120}
              />
              {creer.messageErreur ? <Bandeau type="erreur">{creer.messageErreur}</Bandeau> : null}
              <Bouton type="submit" disabled={ligne.length < 2 || creer.enCours}>
                Enregistrer l’allée
              </Bouton>
            </form>
          ) : null}
          {selection && !tracer ? (
            <div className={styles.pile}>
              <p>
                Place {selection.reference} : cliquez sur sa nouvelle position dans la carte, puis
                confirmez.
              </p>
              {position ? (
                <p>
                  Longitude {position[0].toFixed(6)}, latitude {position[1].toFixed(6)}.
                </p>
              ) : null}
              {deplacer.messageErreur ? (
                <Bandeau type="erreur">{deplacer.messageErreur}</Bandeau>
              ) : null}
              <Bouton
                disabled={!position || deplacer.enCours}
                onClick={() => deplacer.lancer(undefined)}
              >
                Confirmer le déplacement
              </Bouton>
            </div>
          ) : null}
          <EditeurRangee marcheId={marche.id} allees={allees.data ?? []} />
          <ImportAllees marcheId={marche.id} />
        </>
      ) : null}
    </div>
  );
}
